import ChevronFilters from '@assets/icons/chevron-filters.svg';
import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import {
  BaseActionSheet,
  DEFAULT_PADDING,
} from '@/components/base-action-sheet';
import { BORDER, TYPOGRAPHY } from '@/styles';
import { Button } from '@/components/button';
import {
  CheckIn_CreateOrderInvoiceItemIssueMutation,
  CheckIn_CreateOrderInvoiceItemIssueMutationVariables,
  OrderInvoiceCheckInItemStatus,
} from '@/types';
import { CheckInItem } from '@/contexts/check-in-context-types';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_CURRENCY_CODE, formatCurrency } from '@/utils/money';
import { FormInputContainer } from '@/components/form';
import { gql, useMutation } from '@apollo/client';
import { List } from '@/components/list';
import { ListOption } from '@/components/list-item';
import {
  ORDER_INVOICE_CHECK_IN_ITEM_ISSUES_FRAGMENT,
  ORDER_INVOICE_CHECK_IN_ITEMS_FRAGMENT,
  useCheckInContext,
} from '@/contexts/check-in-context';
import { OrderCheckInItemContent } from '@/screens/order-check-in/order-check-in-item-content';
import { QuantitySelector } from '@/components/quantity-selector';
import { SelectInput } from '@/components/select-input';
import { Text, useWindowDimensions, View } from 'react-native';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useItemQueryLocale } from '@/hooks/use-item-query-variables';
import { useToasts } from '@/contexts/toast-context';

export const CREATE_ORDER_INVOICE_CHECK_IN_ITEM_ISSUE_MUTATION = gql`
  mutation CheckIn_CreateOrderInvoiceItemIssue(
    $checkInUUID: uuid!
    $checkInItemUUID: uuid!
    $quantity: Int!
    $status: OrderInvoiceCheckInItemStatus!
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
  ) {
    createOrderInvoiceCheckInItemIssue(
      order_invoice_check_in_id: $checkInUUID
      order_invoice_check_in_item_id: $checkInItemUUID
      quantity: $quantity
      status: $status
    ) {
      order_invoice_check_in_item_issue {
        ...CheckIn_CheckInItemIssuesFields
      }
      order_invoice_check_in_item {
        ...CheckIn_CheckInItemsFields
      }
    }
  }

  ${ORDER_INVOICE_CHECK_IN_ITEMS_FRAGMENT}
  ${ORDER_INVOICE_CHECK_IN_ITEM_ISSUES_FRAGMENT}
`;

interface Props {
  checkInItem: CheckInItem;
  onSubmit: () => void;
}

export const OrderCheckInReportIssueForm: React.FC<Props> = ({
  checkInItem,
  onSubmit,
}) => {
  const [selectingReason, setSelectingReason] = useState(false);
  const [issuesQuantity, setIssuesQuantity] = useState<number>(1);
  const [reason, setReason] = useState<ListOption | null>(null);
  const [styles, theme] = useStyles();
  const {
    amountOfIssues,
    checkInItemUUID,
    checkInUUID,
    priceInMicros,
    shipQuantity,
  } = checkInItem;
  const { currencyCode, refreshOnReportIssue } = useCheckInContext();
  const { showErrorToast } = useToasts();
  const windowDimensions = useWindowDimensions();
  const locale = useItemQueryLocale();
  const { selectedRestaurantUUID: restaurantUUID } =
    useAuthenticatedUserContext();
  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');
  const saltApiIntegrationEnabled = useDynamicConfigValue(
    'salt_api_integration_enabled',
  );

  const [reportIssue, { loading }] = useMutation<
    CheckIn_CreateOrderInvoiceItemIssueMutation,
    CheckIn_CreateOrderInvoiceItemIssueMutationVariables
  >(CREATE_ORDER_INVOICE_CHECK_IN_ITEM_ISSUE_MUTATION, {
    variables: {
      checkInItemUUID,
      checkInUUID,
      locale,
      quantity: issuesQuantity,
      restaurantUUID,
      skipGroupMemberships: !itemGroupsEnabled,
      skipLiveSalt: !saltApiIntegrationEnabled,
      skipSaltApi: !saltApiIntegrationEnabled,
      status: reason?.value as OrderInvoiceCheckInItemStatus,
      useSaltCache: false,
    },
  });

  const reasons = Object.values(OrderInvoiceCheckInItemStatus).map((item) => ({
    title: i18n.t(`check_in.issue_form.reason.${item.toLowerCase()}`),
    value: item,
  }));

  const submitIssue = async () => {
    if (reason) {
      try {
        await reportIssue({
          update: (client, response) => {
            refreshOnReportIssue(
              client,
              checkInItemUUID,
              response.data?.createOrderInvoiceCheckInItemIssue
                .order_invoice_check_in_item,
            );
          },
        });
        onSubmit();
      } catch {
        showErrorToast();
      }
    }
  };

  const actionSheetMaxHeight = Math.floor(windowDimensions.height * 0.6);

  return (
    <>
      <View style={styles.content}>
        <View collapsable={false}>
          <View style={styles.base}>
            <OrderCheckInItemContent checkInItem={checkInItem} />
          </View>
          <FormInputContainer>
            <View style={styles.itemSubtextContainer}>
              <View style={styles.priceContainer}>
                <Text
                  style={TYPOGRAPHY.medium}
                >{`${i18n.t('unit_cost')} `}</Text>
                <Text style={TYPOGRAPHY.pSemiBold}>
                  {formatCurrency(
                    priceInMicros,
                    currencyCode ?? DEFAULT_CURRENCY_CODE,
                  )}
                </Text>
              </View>
              <View style={styles.quantitySelector}>
                <QuantitySelector
                  canDecrementToZero={false}
                  incrementDisabled={
                    issuesQuantity + amountOfIssues === shipQuantity
                  }
                  isFocused={false}
                  onChange={(_, quantity) => setIssuesQuantity(quantity)}
                  quantity={issuesQuantity}
                />
              </View>
            </View>
          </FormInputContainer>
          <FormInputContainer>
            <SelectInput
              inputContainerStyle={{ ...BORDER.primary }}
              label={
                !reason
                  ? i18n.t('credit_request.form.select_reason.placeholder')
                  : ''
              }
              onPress={() => setSelectingReason(true)}
              rightElement={
                <ChevronFilters
                  fill={theme.colors.primary}
                  height={8}
                  width={14}
                />
              }
              testID="check-in-issue-type-selection"
              value={reason?.title ?? ''}
            />
          </FormInputContainer>
        </View>
        <Button
          disabled={!reason}
          loading={loading}
          onPress={submitIssue}
          trackingData={{
            name: 'order_check_in_report_issue_submit_button',
            properties: {
              checkInItemUUID,
              quantity: issuesQuantity,
              selectingReason,
            },
          }}
        >
          {i18n.t('continue')}
        </Button>
      </View>
      <BaseActionSheet
        isVisible={selectingReason}
        onClose={() => setSelectingReason(false)}
      >
        <Text style={TYPOGRAPHY.h2}>
          {i18n.t('check_in.issue_form.select_reason.title')}
        </Text>
        <List
          data={reasons}
          onSelect={(option) => {
            setReason(option);
            setSelectingReason(false);
          }}
          style={[styles.list, { maxHeight: actionSheetMaxHeight }]}
        />
      </BaseActionSheet>
    </>
  );
};

const useStyles = createStyles((_theme) => ({
  base: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 8,
  },
  content: {
    flexGrow: 1,
    justifyContent: 'space-between',
    paddingBottom: 36,
    paddingHorizontal: 16,
  },
  itemSubtextContainer: {
    ...BORDER.primary,
    flexDirection: 'row',
    height: 56,
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  list: {
    marginHorizontal: -DEFAULT_PADDING,
    marginTop: 8,
  },
  priceContainer: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  quantitySelector: {
    alignItems: 'center',
    flexDirection: 'row',
  },
}));
